// LibraryPages.jsx — Librarian's operational pages: book catalog, issue/return, fines.
import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  PlusIcon, XMarkIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon,
  ArrowUturnLeftIcon, ExclamationTriangleIcon, BookOpenIcon,
} from '@heroicons/react/24/outline';

const ACCENT = '#f59e0b';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .lp * { box-sizing:border-box; }
  .lp { font-family:'Sora',sans-serif; color:#1a1d2e; animation:lpFade .4s ease both; }
  @keyframes lpFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .lp-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .lp-title  { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .lp-sub    { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-accent { background:${ACCENT}; color:white; }
  .btn-accent:hover { filter:brightness(0.94); box-shadow:0 4px 14px ${ACCENT}55; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; }
  .btn-ghost:hover { background:#e4e7f0; }
  .btn-red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; }
  .btn-red:hover { background:#ef4444; color:white; }
  .btn-sm { padding:6px 11px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .tabs { display:flex; gap:6px; flex-wrap:wrap; margin-bottom:1.2rem; }
  .tab  { padding:6px 14px; border-radius:99px; border:1.5px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.75rem; font-weight:600; cursor:pointer; color:#6b7194; }
  .tab.active { background:${ACCENT}; color:white; border-color:${ACCENT}; }
  .toolbar { display:flex; gap:.6rem; flex-wrap:wrap; align-items:center; margin-bottom:1.2rem; }
  .search-wrap { position:relative; flex:1; max-width:320px; }
  .search-wrap svg { position:absolute; left:11px; top:50%; transform:translateY(-50%); width:15px; height:15px; stroke:#9094a8; }
  .search-inp { font-family:'Sora',sans-serif; font-size:.8rem; width:100%; padding:8px 12px 8px 32px; border:1.5px solid #e0e4f0; border-radius:10px; background:#f8f9fc; outline:none; }
  .search-inp:focus { border-color:${ACCENT}; background:white; }
  .sel { font-family:'Sora',sans-serif; font-size:.8rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:10px; background:#f8f9fc; outline:none; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1rem; }
  .tbl { width:100%; border-collapse:collapse; }
  .tbl thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .tbl th { padding:10px 14px; text-align:center; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; white-space:nowrap; }
  .tbl td { padding:10px 14px; font-size:.8rem; text-align:center; vertical-align:middle; border-bottom:1px solid #f3f5fb; }
  .tbl tbody tr:hover { background:#fafbff; }
  .empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .loading { display:flex; align-items:center; justify-content:center; height:180px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:${ACCENT}; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  .pill { font-size:.66rem; font-weight:600; padding:3px 9px; border-radius:99px; display:inline-flex; white-space:nowrap; }
  .pill-issued  { background:#eef2ff; color:#3b6ef8; }
  .pill-returned{ background:#ecfdf5; color:#10c98e; }
  .pill-lost    { background:#fef2f2; color:#ef4444; }
  .pill-overdue { background:#fef2f2; color:#ef4444; }
  .pill-pending { background:#fffbeb; color:#f59e0b; }
  .pill-applied { background:#ecfdf5; color:#10c98e; }
  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
  .modal { background:white; border-radius:20px; width:100%; max-width:480px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
  .modal-head { display:flex; align-items:center; justify-content:space-between; padding:16px 20px; border-bottom:1px solid #f0f2f8; }
  .modal-title { font-size:.95rem; font-weight:700; color:#0f1123; }
  .modal-close { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .modal-body { padding:18px 20px; }
  .modal-foot { display:flex; gap:8px; justify-content:flex-end; padding:14px 20px; border-top:1px solid #f0f2f8; flex-wrap:wrap; }
  .form-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
  .form-field { display:flex; flex-direction:column; gap:5px; }
  .form-field.full { grid-column:1/-1; }
  .form-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-input,.form-select,.form-textarea { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-input:focus,.form-select:focus,.form-textarea:focus { border-color:${ACCENT}; background:white; }
  .suggest-list { border:1.5px solid #e0e4f0; border-radius:9px; margin-top:4px; max-height:160px; overflow-y:auto; background:white; }
  .suggest-item { padding:8px 11px; font-size:.8rem; cursor:pointer; border-bottom:1px solid #f3f5fb; }
  .suggest-item:hover { background:#fffbeb; }
  .picked-chip { display:inline-flex; align-items:center; gap:6px; padding:6px 10px; border-radius:9px; background:#fffbeb; border:1px solid #fde68a; font-size:.78rem; font-weight:600; color:#92400e; }
  .book-details { display:flex; flex-wrap:wrap; gap:6px; margin-top:6px; font-size:.72rem; color:#6b7194; }
  .dd-trigger { display:flex; align-items:center; gap:8px; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; cursor:pointer; font-size:.82rem; }
  .dd-trigger:hover { border-color:${ACCENT}; }
  .dd-panel { position:absolute; z-index:20; top:calc(100% + 4px); left:0; right:0; background:white; border:1.5px solid #e0e4f0; border-radius:12px; padding:10px; box-shadow:0 12px 32px rgba(15,17,35,.14); }
  .dd-hint { padding:10px 4px; font-size:.76rem; color:#9094a8; text-align:center; }
  @media (max-width:768px) { .form-grid { grid-template-columns:1fr; } .form-field.full { grid-column:1; } }
`;

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US',{day:'numeric',month:'short',year:'numeric'}) : '—';
const todayStr = () => new Date().toISOString().slice(0,10);
const CATEGORIES = ['General','Textbook','Fiction','Reference','Science','Magazine','Other'];
const FILTER_CATEGORIES = [...CATEGORIES].sort((a,b)=>a.localeCompare(b));
// "Grade 10 — A" (dash skipped when there is no section)
const classLabel = (name, section) => (name && section) ? `${name} — ${section}` : (name || section || '—');
const FINE_PRESETS = ['Overdue Book Fine', 'Damaged Book', 'Lost Book', 'Other'];

// ── Closes a dropdown panel when clicking outside its wrapper. Uses the
// 'click' event rather than 'mousedown': dragging a scrollbar (e.g. the
// modal's own overflow-y scrollbar) fires mousedown+mouseup on the scrollable
// container but never a 'click', and picking an option from a native <select>
// fires mousedown while its native popup is open but the resulting 'click'
// lands back on the <select> itself (which is inside `ref`) — so 'mousedown'
// was closing the panel on both of those interactions, 'click' does not.
const useClickOutside = (ref, onOutside) => {
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'OPTION' || e.target.tagName === 'SELECT') return;
      if (ref.current && !ref.current.contains(e.target)) onOutside();
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [ref, onOutside]);
};

// ── Student picker: class + section dropdowns narrow the roster, plus a
// search bar to find anyone directly when there are too many to scroll ─────
const StudentPicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState('');
  const [section, setSection] = useState('');
  const [q, setQ] = useState('');
  const [students, setStudents] = useState([]);
  const [picked, setPicked] = useState(null);
  const wrapRef = useRef(null);
  useClickOutside(wrapRef, () => setOpen(false));

  useEffect(() => { api.get('/librarian/classes').then(r => setClasses(r.data||[])).catch(()=>{}); }, []);

  // Students are listed only once a class AND section are chosen (class alone
  // is enough when it has no sections), or when the user types a search.
  const selectedClass = classes.find(c => String(c._id) === String(classId));
  const needsSection = !!selectedClass?.sections?.length;
  const canList = !!q.trim() || (!!classId && (!needsSection || !!section));

  useEffect(() => {
    if (!open) { return; }
    if (!canList) { setStudents([]); return; }
    const t = setTimeout(() => {
      const params = new URLSearchParams();
      if (classId) params.set('classId', classId);
      if (section) params.set('section', section);
      if (q.trim()) params.set('search', q.trim());
      api.get(`/librarian/students?${params}`).then(r => setStudents(r.data||[])).catch(()=>{});
    }, 200);
    return () => clearTimeout(t);
  }, [open, canList, classId, section, q]);

  if (value === null && picked) setPicked(null); // parent cleared us externally

  const pick = (s) => { setPicked(s); onChange(s._id); setOpen(false); setQ(''); };
  const clear = () => { setPicked(null); onChange(null); };

  if (picked) {
    return (
      <div className="picked-chip">
        {picked.name} — {classLabel(picked.className, picked.section)} · {picked.admissionNo}
        <button type="button" onClick={clear} style={{border:'none',background:'none',cursor:'pointer',color:'#92400e'}}><XMarkIcon style={{width:14,height:14}}/></button>
      </div>
    );
  }
  return (
    <div ref={wrapRef} style={{position:'relative'}}>
      <div className="dd-trigger" onClick={() => setOpen(o => !o)}>
        <MagnifyingGlassIcon style={{width:14,height:14,stroke:'#9094a8',flexShrink:0}}/>
        <span style={{color:'#9094a8'}}>Select a student…</span>
      </div>
      {open && (
        <div className="dd-panel">
          <div style={{display:'flex',gap:6,marginBottom:8}}>
            <select className="form-select" style={{flex:1}} value={classId} onChange={e=>{setClassId(e.target.value); setSection('');}}>
              <option value="">Select Class</option>
              {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
            <select className="form-select" style={{flex:1}} value={section} onChange={e=>setSection(e.target.value)} disabled={!needsSection}>
              <option value="">Select Section</option>
              {(selectedClass?.sections||[]).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <input autoFocus className="form-input" placeholder="Search by name or admission no…" value={q} onChange={e=>setQ(e.target.value)} />
          <div className="suggest-list" style={{marginTop:8}}>
            {!canList ? <div className="dd-hint">{classId && needsSection ? 'Select a section to see students' : 'Pick a class and section, or search by name to see students'}</div>
            : students.length === 0 ? <div className="dd-hint">No students found</div>
            : students.map(s => (
              <div key={s._id} className="suggest-item" onClick={() => pick(s)}>
                {s.name} — {classLabel(s.className, s.section)} · {s.admissionNo}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Book picker: click to open a dropdown of every book, narrowed by a search
// bar — for Issue (only books with a free copy are selectable) and Fines
// (any book, so the fine can show real book details) ────────────────────────
const BookPicker = ({ value, onChange, requireAvailable = false }) => {
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState(null);
  const wrapRef = useRef(null);
  useClickOutside(wrapRef, () => setOpen(false));

  useEffect(() => {
    if (open && !loaded) {
      api.get('/librarian/books').then(r => setAll(r.data||[])).catch(()=>{});
      setLoaded(true);
    }
  }, [open, loaded]);

  if (value === null && picked) setPicked(null);

  const filtered = all.filter(b => {
    if (requireAvailable && b.availableCopies <= 0) return false;
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return b.title.toLowerCase().includes(s) || (b.author||'').toLowerCase().includes(s) || (b.isbn||'').includes(q);
  });

  const pick = (b) => { setPicked(b); onChange(b); setOpen(false); setQ(''); };
  const clear = () => { setPicked(null); onChange(null); };

  if (picked) {
    return (
      <div>
        <div className="picked-chip">
          <BookOpenIcon style={{width:14,height:14}}/>{picked.title}{requireAvailable ? ` (${picked.availableCopies} available)` : ''}
          <button type="button" onClick={clear} style={{border:'none',background:'none',cursor:'pointer',color:'#92400e'}}><XMarkIcon style={{width:14,height:14}}/></button>
        </div>
        {!requireAvailable && (picked.author || picked.category || picked.isbn || picked.shelfLocation) && (
          <div className="book-details">
            {picked.author && <span>by {picked.author}</span>}
            {picked.category && <span>· {picked.category}</span>}
            {picked.isbn && <span>· ISBN {picked.isbn}</span>}
            {picked.shelfLocation && <span>· Shelf {picked.shelfLocation}</span>}
          </div>
        )}
      </div>
    );
  }
  return (
    <div ref={wrapRef} style={{position:'relative'}}>
      <div className="dd-trigger" onClick={() => setOpen(o => !o)}>
        <MagnifyingGlassIcon style={{width:14,height:14,stroke:'#9094a8',flexShrink:0}}/>
        <span style={{color:'#9094a8'}}>Select a book…</span>
      </div>
      {open && (
        <div className="dd-panel">
          <input autoFocus className="form-input" placeholder="Search by title, author, ISBN…" value={q} onChange={e=>setQ(e.target.value)} />
          <div className="suggest-list" style={{marginTop:8}}>
            {filtered.length === 0 ? <div className="dd-hint">No books found</div>
            : filtered.map(b => (
              <div key={b._id} className="suggest-item" onClick={() => pick(b)}>
                {b.title} {requireAvailable ? `— ${b.availableCopies} available` : `— ${b.author||'Unknown author'}`}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Catalog ──────────────────────────────────────────────────────────────────
const EMPTY_BOOK = { title:'', author:'', isbn:'', category:'General', publisher:'', totalCopies:1, shelfLocation:'' };

export const LibraryCatalog = () => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_BOOK);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (category) params.set('category', category);
    api.get(`/librarian/books?${params}`).then(r => setBooks(r.data||[])).catch(()=>toast.error('Failed to load books')).finally(()=>setLoading(false));
  }, [search, category]);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(EMPTY_BOOK); setModal(true); };
  const openEdit = (b) => { setEditing(b); setForm({ title:b.title, author:b.author, isbn:b.isbn, category:b.category, publisher:b.publisher, totalCopies:b.totalCopies, shelfLocation:b.shelfLocation }); setModal(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      if (editing) { await api.put(`/librarian/books/${editing._id}`, form); toast.success('Book updated'); }
      else { await api.post('/librarian/books', form); toast.success('Book added'); }
      setModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.title}"?`)) return;
    try { await api.delete(`/librarian/books/${b._id}`); toast.success('Book deleted'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  return (
    <div className="lp"><style>{css}</style>
      <div className="lp-header">
        <div><div className="lp-title">Book Catalog</div><div className="lp-sub">Manage the library's book collection</div></div>
        <button className="btn btn-accent" onClick={openAdd}><PlusIcon/>Add Book</button>
      </div>
      <div className="toolbar">
        <div className="search-wrap"><MagnifyingGlassIcon/><input className="search-inp" placeholder="Search by title, author, ISBN…" value={search} onChange={e=>setSearch(e.target.value)} /></div>
        <select className="sel" value={category} onChange={e=>setCategory(e.target.value)}>
          <option value="">All Categories</option>
          {FILTER_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="panel">
        {loading ? <div className="loading"><div className="spinner"/>Loading…</div>
        : books.length===0 ? <div className="empty">No books found. Click "Add Book" to start your catalog.</div>
        : (
          <div style={{overflowX:'auto'}}>
            <table className="tbl">
              <thead><tr><th>Title</th><th>Author</th><th>ISBN</th><th>Publisher</th><th>Category</th><th>Shelf</th><th>Copies</th><th>Actions</th></tr></thead>
              <tbody>
                {books.map(b => (
                  <tr key={b._id}>
                    <td style={{textAlign:'left',fontWeight:600}}>{b.title}</td>
                    <td>{b.author||'—'}</td>
                    <td>{b.isbn||'—'}</td>
                    <td>{b.publisher||'—'}</td>
                    <td>{b.category}</td>
                    <td>{b.shelfLocation||'—'}</td>
                    <td><span style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:700,color:b.availableCopies>0?'#10c98e':'#ef4444'}}>{b.availableCopies}/{b.totalCopies}</span></td>
                    <td>
                      <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(b)}><PencilIcon/></button>
                      <button className="btn btn-red btn-sm" onClick={()=>remove(b)} style={{marginLeft:6}}><TrashIcon/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {modal && createPortal(
        <div className="modal-backdrop" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal">
            <div className="modal-head"><div className="modal-title">{editing?'Edit Book':'Add Book'}</div><button className="modal-close" onClick={()=>setModal(false)}><XMarkIcon/></button></div>
            <form onSubmit={submit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-field full"><label className="form-label">Title *</label><input className="form-input" value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} required/></div>
                  <div className="form-field"><label className="form-label">Author</label><input className="form-input" value={form.author} onChange={e=>setForm(p=>({...p,author:e.target.value}))}/></div>
                  <div className="form-field"><label className="form-label">ISBN</label><input className="form-input" value={form.isbn} onChange={e=>setForm(p=>({...p,isbn:e.target.value}))}/></div>
                  <div className="form-field"><label className="form-label">Category</label>
                    <select className="form-select" value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))}>
                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-field"><label className="form-label">Publisher</label><input className="form-input" value={form.publisher} onChange={e=>setForm(p=>({...p,publisher:e.target.value}))}/></div>
                  <div className="form-field"><label className="form-label">Total Copies</label><input type="number" min="1" className="form-input" value={form.totalCopies} onChange={e=>setForm(p=>({...p,totalCopies:e.target.value}))}/></div>
                  <div className="form-field"><label className="form-label">Shelf Location</label><input className="form-input" value={form.shelfLocation} onChange={e=>setForm(p=>({...p,shelfLocation:e.target.value}))}/></div>
                </div>
              </div>
              <div className="modal-foot">
                <button type="button" className="btn btn-ghost" onClick={()=>setModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-accent" disabled={saving}>{saving?'Saving…':editing?'Update':'Add Book'}</button>
              </div>
            </form>
          </div>
        </div>, document.body)}
    </div>
  );
};

// ── Issue / Return ───────────────────────────────────────────────────────────
export const LibraryIssueReturn = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusF, setStatusF] = useState('');
  const [issueModal, setIssueModal] = useState(false);
  const [pickedBook, setPickedBook] = useState(null);
  const [pickedStudent, setPickedStudent] = useState(null);
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [returnModal, setReturnModal] = useState(null); // issue being returned
  const [lostModal, setLostModal] = useState(null); // issue being marked lost
  const [lostFine, setLostFine] = useState('');
  const [fineAmount, setFineAmount] = useState('');
  const [fineReason, setFineReason] = useState(FINE_PRESETS[0]);

  const load = useCallback(() => {
    setLoading(true);
    const params = statusF ? `?status=${statusF}` : '';
    api.get(`/librarian/issues${params}`).then(r => setIssues(r.data||[])).catch(()=>toast.error('Failed to load issues')).finally(()=>setLoading(false));
  }, [statusF]);
  useEffect(() => { load(); }, [load]);

  const openIssue = () => { setPickedBook(null); setPickedStudent(null); setDueDate(''); setIssueModal(true); };

  const submitIssue = async (e) => {
    e.preventDefault();
    if (!pickedBook || !pickedStudent) { toast.error('Pick both a book and a student'); return; }
    setSaving(true);
    try {
      await api.post('/librarian/issues', { bookId: pickedBook._id, studentId: pickedStudent, dueDate: dueDate || undefined });
      toast.success('Book issued'); setIssueModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const openReturn = (issue) => {
    setReturnModal(issue);
    setFineAmount(''); setFineReason(issue.isOverdue ? FINE_PRESETS[0] : FINE_PRESETS[3]);
  };

  const submitReturn = async () => {
    setSaving(true);
    try {
      await api.put(`/librarian/issues/${returnModal._id}/return`, fineAmount ? { fineAmount: Number(fineAmount), fineReason } : {});
      toast.success('Book returned'); setReturnModal(null); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const openLost = (issue) => { setLostModal(issue); setLostFine(''); };

  const submitLost = async () => {
    setSaving(true);
    try {
      await api.put(`/librarian/issues/${lostModal._id}/lost`, lostFine ? { fineAmount: Number(lostFine) } : {});
      toast.success('Marked as lost'); setLostModal(null); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="lp"><style>{css}</style>
      <div className="lp-header">
        <div><div className="lp-title">Issue / Return</div><div className="lp-sub">Track which books are checked out</div></div>
        <button className="btn btn-accent" onClick={openIssue}><PlusIcon/>Issue a Book</button>
      </div>
      <div className="tabs">
        {['','issued','lost','returned'].map(s => (
          <button key={s||'all'} className={`tab ${statusF===s?'active':''}`} onClick={()=>setStatusF(s)}>{s ? s.charAt(0).toUpperCase()+s.slice(1) : 'All'}</button>
        ))}
      </div>
      <div className="panel">
        {loading ? <div className="loading"><div className="spinner"/>Loading…</div>
        : issues.length===0 ? <div className="empty">No records here.</div>
        : (
          <div style={{overflowX:'auto'}}>
            <table className="tbl">
              <thead><tr><th>Book</th><th>Student</th><th>Class</th><th>Issued</th><th>Due</th>{statusF === '' && <th>Status</th>}<th>Fine</th><th>Actions</th></tr></thead>
              <tbody>
                {issues.map(i => (
                  <tr key={i._id}>
                    <td style={{textAlign:'left',fontWeight:600}}>{i.bookTitle}</td>
                    <td>{i.studentName}</td>
                    <td>{classLabel(i.className, i.section)}</td>
                    <td>{fmtDate(i.issueDate)}</td>
                    <td style={i.isOverdue ? {color:'#ef4444',fontWeight:700} : undefined}>{fmtDate(i.dueDate)}</td>
                    {statusF === '' && (
                      <td>
                        {i.isOverdue ? <span className="pill pill-overdue">Overdue</span> : <span className={`pill pill-${i.status}`}>{i.status}</span>}
                      </td>
                    )}
                    <td>{Number(i.fineAmount) > 0 ? `Rs. ${Number(i.fineAmount).toLocaleString()}` : '—'}</td>
                    <td>
                      {i.status === 'issued' && (
                        <>
                          <button className="btn btn-accent btn-sm" onClick={()=>openReturn(i)}><ArrowUturnLeftIcon/>Return</button>
                          <button className="btn btn-red btn-sm" onClick={()=>openLost(i)} style={{marginLeft:6}}><ExclamationTriangleIcon/>Lost</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {issueModal && createPortal(
        <div className="modal-backdrop" onClick={e=>e.target===e.currentTarget&&setIssueModal(false)}>
          <div className="modal">
            <div className="modal-head"><div className="modal-title">Issue a Book</div><button className="modal-close" onClick={()=>setIssueModal(false)}><XMarkIcon/></button></div>
            <form onSubmit={submitIssue}>
              <div className="modal-body" style={{display:'flex',flexDirection:'column',gap:14}}>
                <div className="form-field">
                  <label className="form-label">Book</label>
                  <BookPicker value={pickedBook} onChange={setPickedBook} requireAvailable/>
                </div>
                <div className="form-field">
                  <label className="form-label">Student</label>
                  <StudentPicker value={pickedStudent} onChange={setPickedStudent} />
                </div>
                <div className="form-field">
                  <label className="form-label">Due Date <span style={{fontWeight:400,textTransform:'none',color:'#b0b5cc'}}>(default 14 days)</span></label>
                  <input type="date" className="form-input" min={todayStr()} value={dueDate} onChange={e=>setDueDate(e.target.value)} />
                </div>
              </div>
              <div className="modal-foot">
                <button type="button" className="btn btn-ghost" onClick={()=>setIssueModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-accent" disabled={saving}>{saving?'Issuing…':'Issue Book'}</button>
              </div>
            </form>
          </div>
        </div>, document.body)}

      {returnModal && createPortal(
        <div className="modal-backdrop" onClick={e=>e.target===e.currentTarget&&setReturnModal(null)}>
          <div className="modal">
            <div className="modal-head"><div className="modal-title">Return Book</div><button className="modal-close" onClick={()=>setReturnModal(null)}><XMarkIcon/></button></div>
            <div className="modal-body" style={{display:'flex',flexDirection:'column',gap:14}}>
              <div style={{fontSize:'.82rem',color:'#6b7194'}}>
                <b>{returnModal.bookTitle}</b> — {returnModal.studentName} ({classLabel(returnModal.className, returnModal.section)})<br/>
                Due {fmtDate(returnModal.dueDate)} {returnModal.isOverdue && <span style={{color:'#ef4444',fontWeight:700}}>(OVERDUE)</span>}
              </div>
              <div className="form-field">
                <label className="form-label">Fine (optional) <span style={{fontWeight:400,textTransform:'none',color:'#b0b5cc'}}>leave blank if none</span></label>
                <input type="number" min="0" className="form-input" placeholder="Amount in Rs." value={fineAmount} onChange={e=>setFineAmount(e.target.value)} />
              </div>
              {fineAmount && (
                <div className="form-field">
                  <label className="form-label">Fine Reason</label>
                  <select className="form-select" value={fineReason} onChange={e=>setFineReason(e.target.value)}>
                    {FINE_PRESETS.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div className="modal-foot">
              <button className="btn btn-ghost" onClick={()=>setReturnModal(null)}>Cancel</button>
              <button className="btn btn-accent" onClick={submitReturn} disabled={saving}>{saving?'Saving…':'Confirm Return'}</button>
            </div>
          </div>
        </div>, document.body)}

      {lostModal && createPortal(
        <div className="modal-backdrop" onClick={e=>e.target===e.currentTarget&&setLostModal(null)}>
          <div className="modal">
            <div className="modal-head"><div className="modal-title">Mark Book as Lost</div><button className="modal-close" onClick={()=>setLostModal(null)}><XMarkIcon/></button></div>
            <div className="modal-body" style={{display:'flex',flexDirection:'column',gap:14}}>
              <div style={{fontSize:'.82rem',color:'#6b7194'}}>
                <b>{lostModal.bookTitle}</b> — {lostModal.studentName} ({classLabel(lostModal.className, lostModal.section)})<br/>
                Due {fmtDate(lostModal.dueDate)} {lostModal.isOverdue && <span style={{color:'#ef4444',fontWeight:700}}>(OVERDUE)</span>}
              </div>
              <div className="form-field">
                <label className="form-label">Fine (optional) <span style={{fontWeight:400,textTransform:'none',color:'#b0b5cc'}}>leave blank if none</span></label>
                <input type="number" min="0" className="form-input" placeholder="Amount in Rs." value={lostFine} onChange={e=>setLostFine(e.target.value)} />
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn btn-ghost" onClick={()=>setLostModal(null)}>Cancel</button>
              <button className="btn btn-red" onClick={submitLost} disabled={saving}>{saving?'Saving…':'Mark as Lost'}</button>
            </div>
          </div>
        </div>, document.body)}
    </div>
  );
};

// ── Fines ────────────────────────────────────────────────────────────────────
export const LibraryFines = () => {
  const [fines, setFines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [pickedStudent, setPickedStudent] = useState(null);
  const [pickedBook, setPickedBook] = useState(null);
  const [reason, setReason] = useState(FINE_PRESETS[1]);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/librarian/fines').then(r => setFines(r.data||[])).catch(()=>toast.error('Failed to load fines')).finally(()=>setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setPickedStudent(null); setPickedBook(null); setReason(FINE_PRESETS[1]); setDescription(''); setAmount(''); setModal(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!pickedStudent) { toast.error('Pick a student'); return; }
    if (!amount || Number(amount) <= 0) { toast.error('Enter a valid amount'); return; }
    setSaving(true);
    try {
      await api.post('/librarian/fines', { studentId: pickedStudent, bookId: pickedBook?._id || undefined, reason, description, amount: Number(amount) });
      toast.success('Fine added'); setModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="lp"><style>{css}</style>
      <div className="lp-header">
        <div><div className="lp-title">Fines</div><div className="lp-sub">Damage, loss, or overdue fines — automatically added to the student's next fee bill</div></div>
        <button className="btn btn-accent" onClick={openAdd}><PlusIcon/>Add Fine</button>
      </div>
      <div className="panel">
        {loading ? <div className="loading"><div className="spinner"/>Loading…</div>
        : fines.length===0 ? <div className="empty">No fines issued yet.</div>
        : (
          <div style={{overflowX:'auto'}}>
            <table className="tbl">
              <thead><tr><th>Student</th><th>Class</th><th>Book</th><th>Reason</th><th>Amount</th><th>Month</th><th>Status</th><th>Date</th></tr></thead>
              <tbody>
                {fines.map(f => (
                  <tr key={f._id}>
                    <td style={{textAlign:'left',fontWeight:600}}>{f.studentName}</td>
                    <td>{classLabel(f.className, f.section)}</td>
                    <td style={{textAlign:'left'}}>
                      {f.bookTitle ? (
                        <>
                          <div style={{fontWeight:600}}>{f.bookTitle}</div>
                          <div style={{fontSize:'.68rem',color:'#9094a8'}}>
                            {[f.bookAuthor, f.bookCategory, f.bookIsbn ? `ISBN ${f.bookIsbn}` : null].filter(Boolean).join(' · ') || '—'}
                          </div>
                        </>
                      ) : '—'}
                    </td>
                    <td>{f.reason}</td>
                    <td style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:700}}>Rs. {Number(f.amount).toLocaleString()}</td>
                    <td>{f.month}</td>
                    <td><span className={`pill pill-${f.status}`}>{f.status === 'applied' ? 'Applied to bill' : 'Pending next bill'}</span></td>
                    <td>{fmtDate(f.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {modal && createPortal(
        <div className="modal-backdrop" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal">
            <div className="modal-head"><div className="modal-title">Add Fine</div><button className="modal-close" onClick={()=>setModal(false)}><XMarkIcon/></button></div>
            <form onSubmit={submit}>
              <div className="modal-body" style={{display:'flex',flexDirection:'column',gap:14}}>
                <div className="form-field">
                  <label className="form-label">Student</label>
                  <StudentPicker value={pickedStudent} onChange={setPickedStudent} />
                </div>
                <div className="form-field">
                  <label className="form-label">Book <span style={{fontWeight:400,textTransform:'none',color:'#b0b5cc'}}>(optional — links the fine to a book and shows its details)</span></label>
                  <BookPicker value={pickedBook} onChange={setPickedBook} />
                </div>
                <div className="form-field">
                  <label className="form-label">Reason</label>
                  <select className="form-select" value={reason} onChange={e=>setReason(e.target.value)}>
                    {FINE_PRESETS.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label className="form-label">Description</label>
                  <textarea className="form-textarea" value={description} onChange={e=>setDescription(e.target.value)} placeholder="Optional details…" />
                </div>
                <div className="form-field">
                  <label className="form-label">Amount (Rs.)</label>
                  <input type="number" min="1" className="form-input" value={amount} onChange={e=>setAmount(e.target.value)} required/>
                </div>
              </div>
              <div className="modal-foot">
                <button type="button" className="btn btn-ghost" onClick={()=>setModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-accent" disabled={saving}>{saving?'Saving…':'Add Fine'}</button>
              </div>
            </form>
          </div>
        </div>, document.body)}
    </div>
  );
};
